import ActionsPopover from "./ActionsPopover";
import {
  typeFormatter,
  sourceFormatter,
  cipTypeFormatter,
} from "../../utils/tableDataFormatter";

const SECTION_ENABLED_KEYS = {
  acid: "acid_enabled",
  alkali: "alkali_enabled",
  solution: "additional_chemical_enabled",
};

const getSectionFormattedValue = (params, section, numeric = false) => {
  const row = params?.data || {};

  if (row?.[SECTION_ENABLED_KEYS?.[section]] === false) {
    return "-";
  }

  const value = params?.value;
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (!numeric) {
    return value;
  }

  const numericValue = parseFloat(value);
  if (Number.isNaN(numericValue)) {
    return "-";
  }

  return numericValue.toFixed(2);
};

export const CIP_TABLE_CONFIG = (
  t,
  temperature,
  mass,
  fetchCIPs,
  showLoader,
  source,
  enableActions = false,
) => {
  const columns = [
    {
      id: 1,
      headerName: t("CIPTableStrings.srNo"),
      field: "srNo",
      flex: 0.5,
      minWidth: 70,
    },
    {
      id: 2,
      headerName: t("HistoricCipTableStrings.start_date"),
      field: "start_date",
      flex: 1,
      minWidth: 160,
    },
    {
      id: 3,
      headerName: t("HistoricCipTableStrings.end_date"),
      field: "end_date",
      flex: 1,
      minWidth: 160,
    },
    {
      id: 4,
      headerName: t("HistoricCipTableStrings.type"),
      field: "type",
      flex: 0.7,
      minWidth: 100,
      valueFormatter: (params) => typeFormatter(params, t),
    },
    {
      id: 5,
      headerName: t("HistoricCipTableStrings.acidPh"),
      field: "acid_cip_ph",
      flex: 1,
      minWidth: 100,
      valueFormatter: (params) =>
        getSectionFormattedValue(params, "acid", true),
      hide: !source,
    },
    {
      id: 6,
      headerName: t("HistoricCipTableStrings.acidTemperature", {
        unit: `\u00B0${temperature}`,
      }),
      field: "acid_cip_temp",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) =>
        getSectionFormattedValue(params, "acid", true),
      hide: !source,
    },
    {
      id: 7,
      headerName: t("HistoricCipTableStrings.acidDuration"),
      field: "acid_cip_duration",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) =>
        getSectionFormattedValue(params, "acid", true),
      hide: !source,
    },
    {
      id: 8,
      headerName: t("HistoricCipTableStrings.acidUsed", { unit: mass }),
      field: "acid_used_per_cip",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) =>
        getSectionFormattedValue(params, "acid", true),
      hide: !source,
    },
    {
      id: 9,
      headerName: t("HistoricCipTableStrings.alkaliPh"),
      field: "alkali_cip_ph",
      flex: 1,
      minWidth: 100,
      valueFormatter: (params) =>
        getSectionFormattedValue(params, "alkali", true),
      hide: !source,
    },
    {
      id: 10,
      headerName: t("HistoricCipTableStrings.alkaliTemperature", {
        unit: `\u00B0${temperature}`,
      }),
      field: "alkali_cip_temp",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) =>
        getSectionFormattedValue(params, "alkali", true),
      hide: !source,
    },
    {
      id: 11,
      headerName: t("HistoricCipTableStrings.alkaliDuration"),
      field: "alkali_cip_duration",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) =>
        getSectionFormattedValue(params, "alkali", true),
      hide: !source,
    },
    {
      id: 12,
      headerName: t("HistoricCipTableStrings.alkaliUsed", { unit: mass }),
      field: "alkali_used_per_cip",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) =>
        getSectionFormattedValue(params, "alkali", true),
      hide: !source,
    },
    {
      id: 13,
      headerName: t("HistoricCipTableStrings.additivePh"),
      field: "cip_solution_ph",
      flex: 1,
      minWidth: 100,
      valueFormatter: (params) =>
        getSectionFormattedValue(params, "solution", true),
      hide: !source,
    },
    {
      id: 14,
      headerName: t("HistoricCipTableStrings.additiveTemperature", {
        unit: `\u00B0${temperature}`,
      }),
      field: "cip_solution_temp",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) =>
        getSectionFormattedValue(params, "solution", true),
      hide: !source,
    },
    {
      id: 15,
      headerName: t("HistoricCipTableStrings.additiveDuration"),
      field: "cip_solution_duration",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) =>
        getSectionFormattedValue(params, "solution", true),
      hide: !source,
    },
    {
      id: 16,
      headerName: t("HistoricCipTableStrings.additiveUsed", { unit: mass }),
      field: "additional_chemical_per_cip",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) =>
        getSectionFormattedValue(params, "solution", true),
      hide: !source,
    },
    {
      id: 17,
      headerName: t("HistoricCipTableStrings.acidChemical"),
      field: "acid_chemical_name",
      flex: 1,
      minWidth: 160,
      valueFormatter: (params) => getSectionFormattedValue(params, "acid"),
      hide: !source,
    },
    {
      id: 18,
      headerName: t("HistoricCipTableStrings.acidPrice"),
      field: "acid_price",
      flex: 1,
      minWidth: 130,
      hide: !source,
      valueFormatter: (params) =>
        getSectionFormattedValue(params, "acid", true),
    },
    {
      id: 19,
      headerName: t("HistoricCipTableStrings.alkaliChemical"),
      field: "alkali_chemical_name",
      flex: 1,
      minWidth: 170,
      valueFormatter: (params) => getSectionFormattedValue(params, "alkali"),
      hide: !source,
    },
    {
      id: 20,
      headerName: t("HistoricCipTableStrings.alkaliPrice"),
      field: "alkali_price",
      flex: 1,
      minWidth: 130,
      hide: !source,
      valueFormatter: (params) =>
        getSectionFormattedValue(params, "alkali", true),
    },
    {
      id: 21,
      headerName: t("HistoricCipTableStrings.additiveChemical"),
      field: "additional_chemical_chemical_name",
      flex: 1,
      minWidth: 180,
      valueFormatter: (params) => getSectionFormattedValue(params, "solution"),
      hide: !source,
    },
    {
      id: 22,
      headerName: t("HistoricCipTableStrings.additivePrice"),
      field: "additional_chemical_price",
      flex: 1,
      minWidth: 140,
      hide: !source,
      valueFormatter: (params) =>
        getSectionFormattedValue(params, "solution", true),
    },
    {
      id: 23,
      headerName: t("HistoricCipTableStrings.is_tool_identified"),
      field: "Source",
      flex: 1,
      minWidth: 150,
      hide: Boolean(source),
      valueGetter: (params) => sourceFormatter(params, t),
    },
    {
      id: 24,
      headerName: t("HistoricCipTableStrings.cipType"),
      field: "cip_type",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => cipTypeFormatter(params, t),
    },
  ];
  if (enableActions) {
    columns.push({
      id: 25,
      width: 60,
      pinned: "right",
      cellRenderer: ({ data }) => (
        <ActionsPopover
          params={data}
          showLoader={showLoader}
          refreshTable={fetchCIPs}
        />
      ),
    });
  }
  return columns;
};
